import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { ArrowLeft, Loader, Plus, Search, Edit2, Server, Cloud, GitBranch, Layers, Database, Check, X, ChevronDown, ChevronRight } from "lucide-react";
import {
  getCMDBApplication,
  updateCMDBApplication,
  deleteCMDBApplication,
  CMDBApplication,
  ApplicationStatus,
  ApplicationCriticality,
  CMDBComponent,
  listAppComponentMappings,
  AppComponentMappingResponse,
  AppComponentDeploymentItem,
  createAppComponentMapping,
  updateAppComponentMapping,
  updateServiceComponentMapping,
  deleteAppComponentMapping,
  addCloudResourceToAppComponent,
  deleteCloudResourceDeployment,
  listAppAssetMappings,
  AssetApplicationMappingNewResponse,
  createAppAssetMapping,
  deleteAppAssetMapping,
  createAssetDeployment,
  deleteAssetDeployment,
  listCiDevices,
  DeviceResponse,
  listCloudProviders,
  CloudProviderResponse,
  unlinkCloudAccountFromApp,
  updateAssetDeployment,
  AssetProtocol,
  AssetRole,
  AssetStatus,
  listAppServices,
  linkServiceToApp,
  unlinkServiceFromApp,
  listServices,
  AppServiceDependency,
  ServiceResponse,
  AppServiceDependencyType,
  saveServiceAssetSelections,
  AwsResourceListResponse,
  AwsServiceTypeResponse,
  listAppResourceTypes,
  listAppResourcePicker,
  listCloudProviderResources,
  listCloudProviderResourceTypes,
  listCMDBComponents,
  listSSPLayers,
  SSPLayer,
} from "../../api/frameworksApi";
import { useToastStore } from "../../stores/toastStore";
import { RBACMultiAction, RBACAction } from "../components/RBACButton";
import { Modal, FormField, FormInput, FormSelect, FormTextarea, ModalFooterButtons } from "../components/Modal";
import { Tip } from "../components/ui/tooltip";
import { Breadcrumb } from "../components/Breadcrumb";
import { CMDBAwsMapping } from "./CMDBAwsMapping";
import { StatusBadge, autoFillPort } from "./cmdb/cmdbUtils";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { AppInfoCard } from "./cmdb/components/AppInfoCard";
import { AppComponentTab } from "./cmdb/components/AppComponentTab";
import { AppServicesTab } from "./cmdb/components/AppServicesTab";
import { AppTopologyView } from "./cmdb/components/AppTopologyView";
import { Dropdown } from "../components/Dropdown";

type MappingWizardPhase = "pick-component" | "add-device";
type CiOnpremResponse = DeviceResponse;

const COMPONENT_LAYER_ORDER = [
  "compute",
  "network",
  "security & compliance",
  "platform",
  "storage",
  "data",
  "middleware & integration",
  "application",
];

function componentLayerRank(layer?: string | null): number {
  if (!layer) return COMPONENT_LAYER_ORDER.length + 1;
  const normalized = layer.trim().toLowerCase();
  const index = COMPONENT_LAYER_ORDER.indexOf(normalized);
  return index === -1 ? COMPONENT_LAYER_ORDER.length : index;
}

/** A device/cloud-resource attachment chosen for a single component in Step 2. */
type WizardAttachment = {
  targetType: "DEVICE" | "CLOUD";
  device: CiOnpremResponse | null;
  resource: AwsResourceListResponse | null;
  cloudAccountFilter: string;
  form: {
    port: string;
    protocol: "TCP" | "UDP" | "HTTP" | "HTTPS" | "GRPC";
    role: "PRIMARY" | "BACKUP" | "STANDBY" | "READ_REPLICA" | "DR" | "ACTIVE" | "PASSIVE";
    status: AssetStatus;
    endpointUrl: string;
    notes: string;
  };
};

export function CMDBApplicationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [application, setApplication] = useState<CMDBApplication | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<"components" | "services" | "cloud-mappings" | "topology">("components");
  const [cloudSubTab, setCloudSubTab] = useState<"accounts" | "resources">("accounts");

  const [showEditApp, setShowEditApp] = useState(false);
  const [editAppForm, setEditAppForm] = useState({
    name: "",
    appCode: "",
    description: "",
    status: "OPERATIONAL" as ApplicationStatus,
    criticality: "LOW" as ApplicationCriticality,
  });

  const [componentMappings, setComponentMappings] = useState<AppComponentMappingResponse[]>([]);
  const [deploymentMappings, setDeploymentMappings] = useState<AssetApplicationMappingNewResponse[]>([]);
  const [linkedCloudAccounts, setLinkedCloudAccounts] = useState<CloudProviderResponse[]>([]);
  const [isRemovingComp, setIsRemovingComp] = useState<Record<string, boolean>>({});

  const [serviceDeps, setServiceDeps]           = useState<AppServiceDependency[]>([]);
  const [showLinkService, setShowLinkService]   = useState(false);
  const [allServices, setAllServices]           = useState<ServiceResponse[]>([]);
  const [loadingServices, setLoadingServices]   = useState(false);
  const [serviceSearch, setServiceSearch]       = useState("");
  const [selectedService, setSelectedService]   = useState<ServiceResponse | null>(null);
  const [serviceDependencyType, setServiceDependencyType] = useState<AppServiceDependencyType>("REQUIRED");
  const [isLinkingService, setIsLinkingService] = useState(false);
  const [isUnlinkingService, setIsUnlinkingService] = useState<Record<string, boolean>>({});
  const [linkServiceAssetChecks, setLinkServiceAssetChecks] = useState<Record<string, boolean>>({});
  const [linkServiceAssetSearch, setLinkServiceAssetSearch] = useState("");

  const [editTarget, setEditTarget] = useState<{ assetId: string; ciName: string } | null>(null);
  const [editForm, setEditForm] = useState({ port: "", protocol: "TCP" as AssetProtocol, role: "PRIMARY" as AssetRole, status: "RUNNING" as AssetStatus });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const [showWizard, setShowWizard] = useState(false);
  const [wizardPhase, setWizardPhase] = useState<MappingWizardPhase>("pick-component");
  // Non-null while the wizard is open in Edit mode (PATCH existing mapping).
  const [editingMapping, setEditingMapping] = useState<AppComponentMappingResponse | null>(null);
  const [layerOverrideReason, setLayerOverrideReason] = useState("");
  // Lightweight "edit layer" modal — replaces the old wizard-based edit flow.
  const [layerEditMapping, setLayerEditMapping] =
    useState<AppComponentMappingResponse | null>(null);
  const [layerEditId, setLayerEditId] = useState("");
  const [layerEditReason, setLayerEditReason] = useState("");
  const [isSavingLayerEdit, setIsSavingLayerEdit] = useState(false);
  // True when the wizard was opened from a single existing component's
  // "Add Device" action (no Step 1 to go Back to).
  const [wizardDeviceOnly, setWizardDeviceOnly] = useState(false);
  // Entered the wizard from a single existing component's panel (hides the
  // multi-select "Back" step). Independent of whether cloud is allowed.
  const [wizardSingleComponent, setWizardSingleComponent] = useState(false);
  // `selectedComponent` is the component currently being edited in Step 2.
  const [selectedComponent, setSelectedComponent] = useState<CMDBComponent | null>(null);
  // Step 1 multi-select: all components chosen to add.
  const [selectedComponents, setSelectedComponents] = useState<CMDBComponent[]>([]);
  const [componentSearch, setComponentSearch] = useState("");
  // Per-component device/resource attachment chosen in Step 2 (keyed by component id).
  const [componentAttachments, setComponentAttachments] = useState<
    Record<string, WizardAttachment>
  >({});
  const [wizardComponentList, setWizardComponentList] = useState<CMDBComponent[]>([]);
  // SSP layers loaded from GET /cmdb/layers for the Step 1 layer selector.
  const [sspLayers, setSspLayers] = useState<SSPLayer[]>([]);
  const [selectedLayerId, setSelectedLayerId] = useState<string>("");
  // Once the user manually changes the layer we stop auto-selecting from
  // component picks so we don't clobber their choice.
  const [layerManuallyChanged, setLayerManuallyChanged] = useState(false);
  const [wizardTargetType, setWizardTargetType] = useState<"DEVICE" | "CLOUD">("DEVICE");
  const [selectedDevice, setSelectedDevice] = useState<CiOnpremResponse | null>(null);
  const [ciOnpremList, setCiOnpremList] = useState<CiOnpremResponse[]>([]);
  const [wizardCloudAccounts, setWizardCloudAccounts] = useState<CloudProviderResponse[]>([]);
  const [wizardCloudAccountId, setWizardCloudAccountId] = useState("");
  const [wizardCloudResources, setWizardCloudResources] = useState<AwsResourceListResponse[]>([]);
  const [wizardCloudResLoading, setWizardCloudResLoading] = useState(false);
  const [wizardResTypes, setWizardResTypes] = useState<AwsServiceTypeResponse[]>([]);
  const [wizardResTypeFilter, setWizardResTypeFilter] = useState("");
  const [wizardResSearch, setWizardResSearch] = useState("");
  const [wizardSelectedResource, setWizardSelectedResource] = useState<AwsResourceListResponse | null>(null);
  const [newDeploymentForm, setNewDeploymentForm] = useState({
    port: "",
    protocol: "TCP" as "TCP" | "UDP" | "HTTP" | "HTTPS" | "GRPC",
    role: "PRIMARY" as "PRIMARY" | "BACKUP" | "STANDBY" | "READ_REPLICA" | "DR" | "ACTIVE" | "PASSIVE",
    status: "RUNNING" as AssetStatus,
    endpointUrl: "",
    notes: "",
  });
  const [isSavingWizard, setIsSavingWizard] = useState(false);
  const [isRemovingDeployment, setIsRemovingDeployment] = useState<Record<string, boolean>>({});

  const [deletingDeployment, setDeletingDeployment] = useState<{ id: string; name: string } | null>(null);
  const [deletingComponent, setDeletingComponent] = useState<AppComponentMappingResponse | null>(null);
  const [deletingService, setDeletingService] = useState<AppServiceDependency | null>(null);
  const [showDeleteApp, setShowDeleteApp] = useState(false);
  const [deletingApp, setDeletingApp] = useState(false);
  const [wizardCloudAccountFilter, setWizardCloudAccountFilter] = useState("");
  const [cloudAddTarget, setCloudAddTarget] = useState<AppComponentMappingResponse | null>(null);
  const [cloudAddAccountFilter, setCloudAddAccountFilter] = useState("");
  const [cloudAddProviderId, setCloudAddProviderId] = useState("");
  const [cloudAddTypeFilter, setCloudAddTypeFilter] = useState("");
  const [cloudAddSearch, setCloudAddSearch] = useState("");
  const [cloudAddTypes, setCloudAddTypes] = useState<AwsServiceTypeResponse[]>([]);
  const [cloudAddResources, setCloudAddResources] = useState<AwsResourceListResponse[]>([]);
  const [cloudAddLoading, setCloudAddLoading] = useState(false);
  const [cloudAddSaving, setCloudAddSaving] = useState(false);
  const [selectedCloudAddResourceId, setSelectedCloudAddResourceId] = useState("");

  const [compTabSearch, setCompTabSearch] = useState("");
  const [componentLayerFilter, setComponentLayerFilter] = useState("");
  const [svcTabSearch, setSvcTabSearch] = useState("");

  useEffect(() => {
    if (id) {
      // Reset all UI state when application ID changes
      setActiveTab("components");
      setShowEditApp(false);
      setEditAppForm({
        name: "",
        appCode: "",
        description: "",
        status: "OPERATIONAL",
        criticality: "LOW",
      });
      setShowWizard(false);
      setWizardPhase("pick-component");
      setShowLinkService(false);
      setEditTarget(null);
      setEditForm({ port: "", protocol: "TCP", role: "PRIMARY", status: "RUNNING" });
      setDeletingDeployment(null);
      setDeletingComponent(null);
      setDeletingService(null);
      setShowDeleteApp(false);
      setCloudAddTarget(null);
      setCompTabSearch("");
      setSvcTabSearch("");
      setServiceSearch("");
      // Load fresh data
      loadData();
    }
  }, [id]);

  const refetchServices = async () => {
    const res = await listAppServices(id!).catch(() => ({ data: [] as AppServiceDependency[] }));
    setServiceDeps(res.data || []);
  };

  const refetchComponents = async () => {
    const res = await listAppComponentMappings(id!).catch(() => ({ data: [] as AppComponentMappingResponse[] }));
    setComponentMappings(res.data || []);
  };

  const refetchAssets = async () => {
    const res = await listAppAssetMappings(id!).catch(() => ({ data: [] as AssetApplicationMappingNewResponse[] }));
    setDeploymentMappings(res.data || []);
  };

  const refetchLinkedAccounts = async () => {
    const res = await listCloudProviders({ applicationId: id! }).catch(() => ({ data: [] as CloudProviderResponse[] }));
    setLinkedCloudAccounts((res.data ?? []).filter((p) => p.provider === "AWS"));
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [appRes, compRes, deployRes, accountsRes, svcRes] = await Promise.all([
        getCMDBApplication(id!),
        listAppComponentMappings(id!).catch(() => ({ data: [] as AppComponentMappingResponse[] })),
        listAppAssetMappings(id!).catch(() => ({ data: [] as AssetApplicationMappingNewResponse[] })),
        listCloudProviders({ applicationId: id! }).catch(() => ({ data: [] as CloudProviderResponse[] })),
        listAppServices(id!).catch(() => ({ data: [] as AppServiceDependency[] })),
      ]);
      setApplication(appRes.data || null);
      setComponentMappings(compRes.data || []);
      setDeploymentMappings(deployRes.data || []);
      setLinkedCloudAccounts((accountsRes.data ?? []).filter((p) => p.provider === "AWS"));
      setServiceDeps(svcRes.data || []);
    } catch (err) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to load application");
      navigate("/cmdb/applications");
    } finally {
      setLoading(false);
    }
  };

  const uniqueComponents = [...new Map(componentMappings.map((c) => [c.componentId, c])).values()];
  const componentLayerOptions = Array.from(
    new Set(
      uniqueComponents
        .map((c) => c.sspLayerName?.trim())
        .filter((layer): layer is string => Boolean(layer)),
    ),
  ).sort(
    (a, b) =>
      componentLayerRank(a) - componentLayerRank(b) || a.localeCompare(b),
  );
  const normalizedComponentSearch = compTabSearch.trim().toLowerCase();
  const filteredComponents = uniqueComponents
    .filter(
      (c) =>
        (!normalizedComponentSearch ||
          c.component.title.toLowerCase().includes(normalizedComponentSearch)) &&
        (!componentLayerFilter || c.sspLayerName === componentLayerFilter),
    )
    .sort(
      (a, b) =>
        componentLayerRank(a.sspLayerName) - componentLayerRank(b.sspLayerName) ||
        (a.sspLayerName ?? "").localeCompare(b.sspLayerName ?? "") ||
        a.component.title.localeCompare(b.component.title),
    );

  const filteredServiceDeps = svcTabSearch
    ? serviceDeps.filter((dep) => dep.service.name.toLowerCase().includes(svcTabSearch.toLowerCase()))
    : serviceDeps;

  // Count mapped component cloud services (group-level), not per-resource rows.
  const mappedComponentServiceKeys = new Set(
    componentMappings
      .flatMap((c) => c.deployments ?? [])
      .filter((d) => d.ciType === "CLOUD")
      .map((d) => [
        d.cloudProviderId ?? "no-provider",
        d.cloudIdentifier ?? d.awsServiceName ?? d.cloudServiceName ?? d.ciId ?? d.assetId,
      ].join("|"))
  );
  const mappedComponentResourcesCount = mappedComponentServiceKeys.size;
  const totalCloudMappingsCount = mappedComponentResourcesCount;

  // Wizard CLOUD: load resource types — reactive to account filter
  useEffect(() => {
    if (!showWizard || wizardTargetType !== "CLOUD" || !id) return;
    (wizardCloudAccountFilter
      ? listCloudProviderResourceTypes(wizardCloudAccountFilter)
      : listAppResourceTypes(id)
    ).then((r) => setWizardResTypes(r.data ?? [])).catch(() => setWizardResTypes([]));
  }, [showWizard, wizardTargetType, wizardCloudAccountFilter, id]);

  // Wizard CLOUD: load resources — reactive to account filter, type filter, search
  useEffect(() => {
    if (!showWizard || wizardTargetType !== "CLOUD") return;
    setWizardCloudResLoading(true);
    const t = setTimeout(() => {
      (wizardCloudAccountFilter
        ? listCloudProviderResources(wizardCloudAccountFilter, {
            cloudIdentifier: wizardResTypeFilter || undefined,
            q: wizardResSearch || undefined,
          })
        : listAppResourcePicker(id!, {
            cloudIdentifier: wizardResTypeFilter || undefined,
            q: wizardResSearch || undefined,
          })
      )
        .then((r) => setWizardCloudResources(r.data ?? []))
        .catch(() => setWizardCloudResources([]))
        .finally(() => setWizardCloudResLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [showWizard, wizardTargetType, wizardResTypeFilter, wizardResSearch, wizardCloudAccountFilter, id]);

  // cloudAdd modal: load resource types — reactive to account filter
  useEffect(() => {
    if (!cloudAddTarget || !id) return;
    (cloudAddAccountFilter
      ? listCloudProviderResourceTypes(cloudAddAccountFilter)
      : listAppResourceTypes(id)
    ).then((r) => setCloudAddTypes(r.data ?? [])).catch(() => setCloudAddTypes([]));
  }, [cloudAddTarget, cloudAddAccountFilter, id]);

  // cloudAdd modal: load resources — reactive to account filter, type filter, search
  useEffect(() => {
    if (!cloudAddTarget) return;
    setCloudAddLoading(true);
    const t = setTimeout(() => {
      (cloudAddAccountFilter
        ? listCloudProviderResources(cloudAddAccountFilter, {
            cloudIdentifier: cloudAddTypeFilter || undefined,
            q: cloudAddSearch || undefined,
          })
        : listAppResourcePicker(id!, {
            cloudIdentifier: cloudAddTypeFilter || undefined,
            q: cloudAddSearch || undefined,
          })
      )
        .then((r) => setCloudAddResources(r.data ?? []))
        .catch(() => setCloudAddResources([]))
        .finally(() => setCloudAddLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [cloudAddTarget, cloudAddTypeFilter, cloudAddSearch, cloudAddAccountFilter, id]);

  const DEFAULT_DEPLOYMENT_FORM = {
    port: "",
    protocol: "TCP" as const,
    role: "PRIMARY" as const,
    status: "RUNNING" as const,
    endpointUrl: "",
    notes: "",
  };

  const resetWizard = () => {
    setShowWizard(false);
    setWizardPhase("pick-component");
    setWizardDeviceOnly(false);
    setWizardSingleComponent(false);
    setEditingMapping(null);
    setLayerOverrideReason("");
    setSelectedComponent(null);
    setSelectedComponents([]);
    setComponentSearch("");
    setComponentAttachments({});
    setSelectedDevice(null);
    setWizardTargetType("DEVICE");
    setWizardCloudAccountId("");
    setWizardCloudAccountFilter("");
    setWizardCloudResources([]);
    setWizardResTypes([]);
    setWizardResTypeFilter("");
    setWizardResSearch("");
    setWizardSelectedResource(null);
    setNewDeploymentForm({ ...DEFAULT_DEPLOYMENT_FORM });
    setSelectedLayerId("");
    setLayerManuallyChanged(false);
  };

  // Best-effort match a component to an SSP layer: prefer the component's
  // own layerId, then match componentType against layer slug / displayName.
  const findLayerForComponent = (c: CMDBComponent): string => {
    if (!sspLayers.length) return "";
    if (c.layerId && sspLayers.some((l) => l.id === c.layerId)) return c.layerId;
    const compLayerId = c.layer?.id;
    if (compLayerId && sspLayers.some((l) => l.id === compLayerId)) return compLayerId;
    const type = (c.componentType || "").toLowerCase();
    if (!type) return "";
    const match = sspLayers.find((l) => {
      const slug = (l.slug || "").toLowerCase();
      const name = (l.displayName || "").toLowerCase();
      return slug === type || name === type || slug.includes(type) || type.includes(slug);
    });
    return match?.id ?? "";
  };

  // Toggle a component in/out of the Step 1 multi-select.
  const toggleComponentSelection = (c: CMDBComponent) => {
    setSelectedComponents((prev) => {
      const alreadySelected = prev.some((x) => x.id === c.id);
      if (alreadySelected) return prev.filter((x) => x.id !== c.id);
      // On add: auto-select the matching layer only if the user hasn't picked one manually.
      if (!layerManuallyChanged) {
        const suggested = findLayerForComponent(c);
        if (suggested) setSelectedLayerId(suggested);
        else if (!selectedLayerId && sspLayers.length > 0) {
          setSelectedLayerId(sspLayers[0].id);
        }
      }
      return [...prev, c];
    });
  };

  const removeSelectedComponent = (compId: string) => {
    setSelectedComponents((prev) => prev.filter((x) => x.id !== compId));
    setComponentAttachments((prev) => {
      const next = { ...prev };
      delete next[compId];
      return next;
    });
    if (selectedComponent?.id === compId) setSelectedComponent(null);
  };

  // Snapshot the in-progress picker into the active component's attachment.
  const buildActiveAttachment = (): WizardAttachment => ({
    targetType: wizardTargetType,
    device: selectedDevice,
    resource: wizardSelectedResource,
    cloudAccountFilter: wizardCloudAccountFilter,
    form: { ...newDeploymentForm },
  });

  // Open the device picker for one component in Step 2 (saving the previously
  // active one and restoring this component's saved selection, if any).
  const openComponentPicker = (c: CMDBComponent) => {
    setComponentAttachments((prev) => {
      if (!selectedComponent) return prev;
      return { ...prev, [selectedComponent.id]: buildActiveAttachment() };
    });
    if (selectedComponent?.id === c.id) {
      // Collapsing the currently-open component.
      setSelectedComponent(null);
      return;
    }
    const saved = componentAttachments[c.id];
    setWizardTargetType(saved?.targetType ?? "DEVICE");
    setSelectedDevice(saved?.device ?? null);
    setWizardSelectedResource(saved?.resource ?? null);
    setWizardCloudAccountFilter(saved?.cloudAccountFilter ?? "");
    setWizardResTypeFilter("");
    setWizardResSearch("");
    setNewDeploymentForm(saved?.form ?? { ...DEFAULT_DEPLOYMENT_FORM });
    setSelectedComponent(c);
  };

  // Whether a component has a device/cloud resource chosen. For the currently
  // open ("active") component the live picker state is used; others use their
  // saved attachment.
  const componentHasAttachment = (compId: string): boolean => {
    if (selectedComponent?.id === compId) {
      return wizardTargetType === "DEVICE"
        ? !!selectedDevice
        : !!wizardSelectedResource;
    }
    const a = componentAttachments[compId];
    if (!a) return false;
    return a.targetType === "DEVICE" ? !!a.device : !!a.resource;
  };

  // Selected components still missing a device/resource (Step 2 is mandatory).
  const componentsMissingAttachment = selectedComponents.filter(
    (c) => !componentHasAttachment(c.id),
  );

  const handleSaveOnPremMapping = async () => {
    if (selectedComponents.length === 0) {
      useToastStore.getState().showError("Error", "Select at least one component");
      return;
    }
    if (componentsMissingAttachment.length > 0) {
      useToastStore
        .getState()
        .showError(
          "Error",
          `Select a device or cloud resource for: ${componentsMissingAttachment
            .map((c) => c.title)
            .join(", ")}`,
        );
      return;
    }

    // Merge the currently-open picker into the attachments map.
    const attachments: Record<string, WizardAttachment> = {
      ...componentAttachments,
    };
    if (selectedComponent) attachments[selectedComponent.id] = buildActiveAttachment();

    // Validate ports up front so we fail before any network calls.
    for (const comp of selectedComponents) {
      const a = attachments[comp.id];
      if (a?.form.port) {
        const parsedPort = Number(a.form.port);
        if (!Number.isInteger(parsedPort) || parsedPort < 1 || parsedPort > 65535) {
          useToastStore
            .getState()
            .showError("Error", `Port for ${comp.title} must be a number between 1 and 65535`);
          return;
        }
      }
    }

    setIsSavingWizard(true);
    let infraCount = 0;
    try {
      for (const comp of selectedComponents) {
        const a = attachments[comp.id];
        try {
          await createAppComponentMapping(id!, {
            componentId: comp.id,
            notes: a?.form.notes || undefined,
            layerId: selectedLayerId || undefined,
          });
        } catch (err: any) {
          if (!String(err?.message ?? "").includes("409")) throw err;
        }

        const hasInfra =
          (a?.targetType === "DEVICE" && a.device) ||
          (a?.targetType === "CLOUD" && a.resource);
        if (!a || !hasInfra) continue;

        const assetPayload: any = {
          componentId: comp.id,
          role: a.form.role,
          status: a.form.status,
          protocol: a.form.protocol,
        };
        if (a.form.port) assetPayload.port = Number(a.form.port);
        if (a.form.endpointUrl) assetPayload.endpointUrl = a.form.endpointUrl;
        if (a.form.notes) assetPayload.notes = a.form.notes;

        if (a.targetType === "DEVICE" && a.device) {
          assetPayload.ciType = "ON_PREMISES";
          assetPayload.ciOnpremId = a.device.id;
        } else if (a.targetType === "CLOUD" && a.resource) {
          assetPayload.ciType = "CLOUD";
          assetPayload.ciCloudProviderId = a.resource.cloudProviderId;
          assetPayload.cloudResourceId = a.resource.id;
          assetPayload.applicationId = id!;
        }

        const assetRes = await createAssetDeployment(assetPayload);
        await createAppAssetMapping(id!, { assetId: assetRes.data.id });
        infraCount += 1;
      }

      const compCount = selectedComponents.length;
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `${compCount} component${compCount !== 1 ? "s" : ""} added${
            infraCount > 0
              ? ` with ${infraCount} resource${infraCount !== 1 ? "s" : ""}`
              : ""
          }`,
        );
      resetWizard();
      await refetchComponents();
      await refetchAssets();
    } catch (err) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to save mapping");
    } finally {
      setIsSavingWizard(false);
    }
  };

  const handleRemoveDeploymentRow = (assetId: string) => {
    const appDep = deploymentMappings.find((m) => m.assetId === assetId);
    const compDep = componentMappings
      .flatMap((c) => c.deployments ?? [])
      .find((d) => d.assetId === assetId);
    setDeletingDeployment({
      id: assetId,
      name:
        appDep?.ciName ||
        compDep?.ciName ||
        compDep?.cloudResourceName ||
        compDep?.cloudResourceIdentifier ||
        compDep?.ciIdentifier ||
        "this deployment",
    });
  };

  const confirmRemoveDeployment = async () => {
    if (!deletingDeployment) return;
    const assetId = deletingDeployment.id;
    setIsRemovingDeployment((p) => ({ ...p, [assetId]: true }));
    try {
      const cloudDep = componentMappings
        .flatMap((c) => c.deployments ?? [])
        .find((d) => d.assetId === assetId && d.ciType === "CLOUD");

      if (cloudDep) {
        await deleteCloudResourceDeployment(assetId);
        setComponentMappings((prev) =>
          prev.map((c) => ({
            ...c,
            deployments: (c.deployments ?? []).filter((d) => d.assetId !== assetId),
          })),
        );
      } else {
        const appAssetMapping = deploymentMappings.find((m) => m.assetId === assetId);
        if (appAssetMapping) {
          await deleteAppAssetMapping(appAssetMapping.id);
        } else {
          await deleteAssetDeployment(assetId);
        }
        await Promise.all([refetchComponents(), refetchAssets()]);
      }
      useToastStore.getState().showSuccess("Success", "Deployment removed");
      setDeletingDeployment(null);
    } catch {
      useToastStore.getState().showError("Error", "Failed to remove deployment");
    } finally {
      setIsRemovingDeployment((p) => ({ ...p, [assetId]: false }));
    }
  };

  const openEditApp = () => {
    if (!application) return;
    setEditAppForm({
      name: application.name,
      appCode: application.appCode || "",
      description: application.description || "",
      status: application.status,
      criticality: application.criticality,
    });
    setShowEditApp(true);
  };

  const handleSaveApp = async () => {
    if (!application) return;
    if (!editAppForm.name.trim()) {
      useToastStore.getState().showError("Error", "Name is required");
      return;
    }
    try {
      const res = await updateCMDBApplication(application.id, {
        name: editAppForm.name,
        appCode: editAppForm.appCode || null,
        description: editAppForm.description || null,
        status: editAppForm.status,
        criticality: editAppForm.criticality,
      });
      setApplication(res.data);
      setShowEditApp(false);
      useToastStore.getState().showSuccess("Success", "Application updated");
    } catch (err) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to update application");
    }
  };

  const handleDeleteApplication = async () => {
    if (!application) return;
    setDeletingApp(true);
    try {
      await deleteCMDBApplication(application.id);
      useToastStore.getState().showSuccess("Success", "Application deleted.");
      setShowDeleteApp(false);
      navigate("/cmdb/applications");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("CMDB-0034-E")) {
        useToastStore.getState().showError("Error", "This application is linked to one or more System Security Plans and cannot be deleted. Remove the SSP association first.");
      } else if (msg.includes("404")) {
        useToastStore.getState().showError("Error", "Application not found.");
      } else {
        useToastStore.getState().showError("Error", "Failed to delete application");
      }
      setShowDeleteApp(false);
    } finally {
      setDeletingApp(false);
    }
  };

  const handleRemoveOnPremComponent = (comp: AppComponentMappingResponse) => {
    setDeletingComponent(comp);
  };

  const confirmRemoveComponent = async () => {
    if (!deletingComponent) return;
    const comp = deletingComponent;
    setIsRemovingComp((p) => ({ ...p, [comp.id]: true }));
    try {
      await deleteAppComponentMapping(comp.id);
      await Promise.all([refetchComponents(), refetchAssets()]);
      setApplication((prev) => prev ? { ...prev, componentCount: Math.max(0, (prev.componentCount ?? 1) - 1) } : prev);
      useToastStore.getState().showSuccess("Success", "Component removed");
      setDeletingComponent(null);
    } catch (err) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to remove component");
    } finally {
      setIsRemovingComp((p) => ({ ...p, [comp.id]: false }));
    }
  };

  const openAddDeviceToComponent = async (comp: AppComponentMappingResponse) => {
    resetWizard();
    try {
      const [devRes, cloudRes] = await Promise.allSettled([listCiDevices(), listCloudProviders()]);
      if (devRes.status === "fulfilled") setCiOnpremList(devRes.value.data || []);
      if (cloudRes.status === "fulfilled") setWizardCloudAccounts(cloudRes.value.data || []);
    } catch { /* ignore */ }
    const single = { id: comp.componentId, title: comp.component.title } as any;
    // Step 2 renders from selectedComponents; seed it so the picker shows for
    // this single existing component (entry point from the component panel).
    setSelectedComponents([single]);
    setSelectedComponent(single);
    // On-prem components attach on-prem devices only; cloud components may
    // attach either a device or a cloud resource (show both options).
    const deps = comp.deployments ?? [];
    const isCloudComponent =
      comp.source === "CLOUD" || deps.some((d) => d.ciType === "CLOUD");
    setWizardDeviceOnly(!isCloudComponent);
    setWizardSingleComponent(true);
    setWizardPhase("add-device");
    setShowWizard(true);
  };

  const openAddCloudResourceToComponent = (comp: AppComponentMappingResponse) => {
    setCloudAddTarget(comp);
    setSelectedCloudAddResourceId("");
    setCloudAddSearch("");
    setCloudAddTypeFilter("");
    setCloudAddAccountFilter("");
  };

  // Open the lightweight "edit layer" modal for an existing mapping.
  // Only the layer (with a reason for the change) can be edited.
  const openEditComponent = async (comp: AppComponentMappingResponse) => {
    let layers = sspLayers;
    if (!layers.length) {
      try {
        const res = await listSSPLayers();
        layers = (res.data || [])
          .filter((l) => l.active !== false)
          .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
        setSspLayers(layers);
      } catch {
        /* ignore */
      }
    }
    const currentSlug = (comp.sspLayerSlug || "").toLowerCase();
    const currentName = (comp.sspLayerName || "").toLowerCase();
    const matched = currentSlug
      ? layers.find((l) => l.slug?.toLowerCase() === currentSlug)
      : currentName
        ? layers.find((l) => l.displayName?.toLowerCase() === currentName)
        : undefined;
    setLayerEditMapping(comp);
    setLayerEditId(matched?.id ?? "");
    setLayerEditReason("");
  };

  const closeEditLayer = () => {
    if (isSavingLayerEdit) return;
    setLayerEditMapping(null);
    setLayerEditId("");
    setLayerEditReason("");
  };

  const handleSaveEditLayer = async () => {
    if (!layerEditMapping) return;
    const newLayer = sspLayers.find((l) => l.id === layerEditId);
    const newSlug = (newLayer?.slug ?? "").toLowerCase();
    const originalSlug = (layerEditMapping.sspLayerSlug ?? "").toLowerCase();
    const layerChanged = !!newSlug && newSlug !== originalSlug;

    if (!layerChanged) {
      useToastStore
        .getState()
        .showInfo("No changes", "Pick a different layer to update this component");
      return;
    }

    const reason = layerEditReason.trim();
    if (!reason) {
      useToastStore
        .getState()
        .showError("Error", "Enter a reason for changing the component layer");
      return;
    }

    setIsSavingLayerEdit(true);
    try {
      const isService = layerEditMapping.source === "SERVICE";
      const patch = { layerSlug: newSlug, layerOverrideReason: reason };
      const res = isService
        ? await updateServiceComponentMapping(layerEditMapping.id, patch)
        : await updateAppComponentMapping(layerEditMapping.id, patch);
      setComponentMappings((prev) =>
        prev.map((c) =>
          c.id === layerEditMapping.id ? { ...c, ...res.data } : c,
        ),
      );
      useToastStore.getState().showSuccess("Success", "Component layer updated");
      setLayerEditMapping(null);
      setLayerEditId("");
      setLayerEditReason("");
      await refetchComponents();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("CMDB-0056-E")) {
        useToastStore
          .getState()
          .showError("Error", "Selected layer was not found. Refresh and try again.");
      } else if (msg.includes("GRC-0001-E")) {
        useToastStore
          .getState()
          .showError("Error", "A reason is required when changing the component layer.");
      } else if (
        msg.includes("CMDB-0006-E") ||
        msg.includes("CMDB-0028-E") ||
        msg.includes("404")
      ) {
        useToastStore
          .getState()
          .showError("Error", "This component mapping no longer exists.");
      } else {
        useToastStore
          .getState()
          .showError(
            "Error",
            err instanceof Error ? err.message : "Failed to update component",
          );
      }
    } finally {
      setIsSavingLayerEdit(false);
    }
  };

  // PATCH the existing mapping using the wizard's current values.
  const handleUpdateMapping = async () => {
    if (!editingMapping) return;
    const compId = editingMapping.componentId;
    const attachment = componentAttachments[compId];
    const notes = attachment?.form.notes?.trim() ?? "";
    const originalNotes = (editingMapping.notes ?? "").trim();
    const newLayer = sspLayers.find((l) => l.id === selectedLayerId);
    const originalSlug = (editingMapping.sspLayerSlug ?? "").toLowerCase();
    const newSlug = (newLayer?.slug ?? "").toLowerCase();

    const payload: {
      notes?: string;
      layerSlug?: string;
      layerOverrideReason?: string;
    } = {};

    if (notes !== originalNotes) payload.notes = notes;

    if (newSlug && newSlug !== originalSlug) {
      const reason = layerOverrideReason.trim();
      if (!reason) {
        useToastStore
          .getState()
          .showError("Error", "Enter a reason for changing the component layer");
        return;
      }
      payload.layerSlug = newSlug;
      payload.layerOverrideReason = reason;
    }

    if (Object.keys(payload).length === 0) {
      useToastStore
        .getState()
        .showInfo("No changes", "Nothing to update for this component");
      return;
    }

    setIsSavingWizard(true);
    try {
      const isService = editingMapping.source === "SERVICE";
      const res = isService
        ? await updateServiceComponentMapping(editingMapping.id, payload)
        : await updateAppComponentMapping(editingMapping.id, payload);
      setComponentMappings((prev) =>
        prev.map((c) => (c.id === editingMapping.id ? { ...c, ...res.data } : c)),
      );
      useToastStore.getState().showSuccess("Success", "Component updated");
      resetWizard();
      await refetchComponents();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("CMDB-0056-E")) {
        useToastStore.getState().showError("Error", "Selected layer was not found. Refresh and try again.");
      } else if (msg.includes("GRC-0001-E")) {
        useToastStore.getState().showError("Error", "A reason is required when changing the component layer.");
      } else if (msg.includes("CMDB-0006-E") || msg.includes("CMDB-0028-E") || msg.includes("404")) {
        useToastStore.getState().showError("Error", "This component mapping no longer exists.");
      } else {
        useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to update component");
      }
    } finally {
      setIsSavingWizard(false);
    }
  };

  const handleConfirmAddCloudResource = async () => {
    if (!cloudAddTarget || !selectedCloudAddResourceId) return;
    setCloudAddSaving(true);
    try {
      const res = await addCloudResourceToAppComponent(cloudAddTarget.id, {
        cloudResourceId: selectedCloudAddResourceId,
      });
      setComponentMappings((prev) =>
        prev.map((c) =>
          c.id === cloudAddTarget.id
            ? { ...c, deployments: [...(c.deployments ?? []), res.data] }
            : c,
        ),
      );
      useToastStore.getState().showSuccess("Success", "Cloud resource added");
      setCloudAddTarget(null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      if (msg.includes("CMDB-0035-E")) {
        useToastStore.getState().showError("Error", "This resource is already linked to this component.");
      } else if (msg.includes("404")) {
        useToastStore.getState().showError("Error", "Cloud resource not found.");
      } else {
        useToastStore.getState().showError("Error", "Something went wrong, please try again.");
      }
    } finally {
      setCloudAddSaving(false);
    }
  };

  const handleUnlinkCloudAccountFromRow = async (providerId: string) => {
    try {
      await unlinkCloudAccountFromApp(id!, providerId);
      useToastStore.getState().showSuccess("Success", "Cloud account unlinked successfully");
      await Promise.all([refetchComponents(), refetchAssets(), refetchLinkedAccounts()]);
    } catch (err) {
      useToastStore.getState().showError("Error", "Failed to unlink cloud account");
    }
  };

  async function openLinkServiceModal() {
    setShowLinkService(true);
    setSelectedService(null);
    setServiceSearch("");
    setServiceDependencyType("REQUIRED");
    setLinkServiceAssetChecks({});
    setLinkServiceAssetSearch("");
    setLoadingServices(true);
    try {
      const r = await listServices();
      setAllServices(r.data ?? []);
    } catch { setAllServices([]); }
    finally { setLoadingServices(false); }
  }

  function initLinkServiceAssets(svc: ServiceResponse) {
    const assets = (svc.components ?? []).flatMap((c) => c.deployments ?? []);
    const checks: Record<string, boolean> = {};
    assets.forEach((d) => { if (d.assetId) checks[d.assetId] = true; });
    setLinkServiceAssetChecks(checks);
    setLinkServiceAssetSearch("");
  }

  async function handleLinkService() {
    if (!selectedService) return;
    setIsLinkingService(true);
    try {
      await linkServiceToApp(id!, { serviceId: selectedService.id, dependencyType: serviceDependencyType });
      // Save asset selection only if the user unchecked some assets
      const allIds = Object.keys(linkServiceAssetChecks);
      const selectedIds = allIds.filter((assetId) => linkServiceAssetChecks[assetId]);
      if (allIds.length > 0 && selectedIds.length < allIds.length) {
        await saveServiceAssetSelections(id!, selectedService.id, selectedIds).catch(() => null);
      }
      useToastStore.getState().showSuccess("Success", `${selectedService.name} linked`);
      setShowLinkService(false);
      await refetchServices();
    } catch (err) {
      useToastStore.getState().showError("Error", err instanceof Error ? err.message : "Failed to link service");
    } finally { setIsLinkingService(false); }
  }

  async function handleUnlinkService(dep: AppServiceDependency) {
    setDeletingService(dep);
  }

  const confirmUnlinkService = async () => {
    if (!deletingService) return;
    const dep = deletingService;
    setIsUnlinkingService((p) => ({ ...p, [dep.id]: true }));
    try {
      await unlinkServiceFromApp(dep.id);
      setServiceDeps((prev) => prev.filter((d) => d.id !== dep.id));
      useToastStore.getState().showSuccess("Success", "Service removed");
      setDeletingService(null);
    } catch {
      useToastStore.getState().showError("Error", "Failed to remove service");
    } finally { setIsUnlinkingService((p) => ({ ...p, [dep.id]: false })); }
  };

  function openEditModal(d: any) {
    setEditTarget({ assetId: d.assetId, ciName: d.ciName });
    setEditForm({
      port: d.port != null ? String(d.port) : "",
      protocol: (d.protocol as AssetProtocol) || "TCP",
      role: (d.role as AssetRole) || "PRIMARY",
      status: (d.status as AssetStatus) || "RUNNING",
    });
  }

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setIsSavingEdit(true);
    try {
      await updateAssetDeployment(editTarget.assetId, {
        port: editForm.port ? Number(editForm.port) : null,
        protocol: editForm.protocol,
        role: editForm.role,
        status: editForm.status,
      });
      await refetchComponents();
      await refetchAssets();
      setEditTarget(null);
      useToastStore.getState().showSuccess("Success", "Component updated");
    } catch {
      useToastStore.getState().showError("Error", "Failed to update component");
    } finally {
      setIsSavingEdit(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader className="size-8 text-muted-foreground animate-spin" />
      </div>
    );
  }

  if (!application) {
    return (
      <div className="p-6 space-y-4">
        <Breadcrumb
          parent="Applications"
          parentTo="/cmdb/applications"
          current="Not found"
        />
        <div className="rounded-lg border border-border bg-muted/50 p-6 text-center">
          <p className="text-muted-foreground">Application not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumb
        parent="Applications"
        parentTo="/cmdb/applications"
        current={application.name}
        badge={
          <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${
            application.isActive
              ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
              : "bg-red-500/15 text-red-400 border border-red-500/25"
          }`}>
            {application.isActive ? "Active" : "Inactive"}
          </span>
        }
      />

      <AppInfoCard
        application={application}
        onEdit={openEditApp}
        onDelete={() => setShowDeleteApp(true)}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}
      />

      {activeTab === "topology" && id && (
        <AppTopologyView appId={id} />
      )}

      {activeTab !== "topology" && (
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-border">
          <div className="flex items-center">
            <button
              onClick={() => setActiveTab("components")}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "components"
                  ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              }`}
            >
              Components
              {componentMappings.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === "components"
                    ? "bg-indigo-500/20 text-indigo-600 dark:text-indigo-400"
                    : "bg-muted text-muted-foreground"
                }`}>{componentMappings.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("services")}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "services"
                  ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              }`}
            >
              <Layers className="size-3.5" />
              Services
              {serviceDeps.length > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === "services"
                    ? "bg-indigo-500/20 text-indigo-600 dark:text-indigo-400"
                    : "bg-muted text-muted-foreground"
                }`}>{serviceDeps.length}</span>
              )}
            </button>
            <button
              onClick={() => setActiveTab("cloud-mappings")}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === "cloud-mappings"
                  ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                  : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
              }`}
            >
              <GitBranch className="size-3.5" />
              Cloud Mappings
              {totalCloudMappingsCount > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === "cloud-mappings"
                    ? "bg-indigo-500/20 text-indigo-600 dark:text-indigo-400"
                    : "bg-muted text-muted-foreground"
                }`}>{totalCloudMappingsCount}</span>
              )}
            </button>
          </div>

          {activeTab === "components" && (
            <RBACMultiAction checks={[["cmdb", "create"], ["cmdb", "update"]]} type="any">
              <button
                onClick={async () => {
                  resetWizard();
                  try {
                    const [devRes, cloudRes, compRes, layerRes] = await Promise.allSettled([
                      listCiDevices(),
                      listCloudProviders(),
                      listCMDBComponents(),
                      listSSPLayers(),
                    ]);
                    if (devRes.status === "fulfilled") setCiOnpremList(devRes.value.data || []);
                    if (cloudRes.status === "fulfilled") setWizardCloudAccounts(cloudRes.value.data || []);
                    if (compRes.status === "fulfilled") setWizardComponentList(compRes.value.data || []);
                    if (layerRes.status === "fulfilled") {
                      const layers = (layerRes.value.data || [])
                        .filter((l) => l.active !== false)
                        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
                      setSspLayers(layers);
                    }
                  } catch { /* ignore */ }
                  setShowWizard(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                <Plus className="size-4" />
                Add Component
              </button>
            </RBACMultiAction>
          )}
          {activeTab === "services" && (
            <RBACMultiAction checks={[["cmdb", "create"], ["cmdb", "update"]]} type="any">
              <button
                onClick={openLinkServiceModal}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
              >
                <Plus className="size-4" />
                Link Service
              </button>
            </RBACMultiAction>
          )}
        </div>

        {activeTab === "components" && (
          <div className="space-y-4">
            {componentMappings.length > 0 && (
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <div className="relative w-full sm:max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search components"
                    value={compTabSearch}
                    onChange={(e) => setCompTabSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
                  />
                </div>
                <Dropdown
                  value={componentLayerFilter}
                  onChange={setComponentLayerFilter}
                  className="w-full sm:w-[220px]"
                  options={[
                    { value: "", label: "All layers" },
                    ...componentLayerOptions.map((layer) => ({
                      value: layer,
                      label: layer,
                    })),
                  ]}
                />
              </div>
            )}
            <AppComponentTab
              components={filteredComponents}
              onRemoveComponent={handleRemoveOnPremComponent}
              onAddDevice={openAddDeviceToComponent}
              onAddCloudResource={openAddCloudResourceToComponent}
              onEditComponent={openEditComponent}
              onEditDeployment={openEditModal}
              onRemoveDeployment={(d) => handleRemoveDeploymentRow(d.assetId)}
              removingCompIds={isRemovingComp}
              removingDeploymentIds={isRemovingDeployment}
            />
          </div>
        )}

        {activeTab === "services" && (
          <div className="space-y-4">
            {serviceDeps.length > 0 && (
              <div className="relative max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search services…"
                  value={svcTabSearch}
                  onChange={(e) => setSvcTabSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
                />
              </div>
            )}
            <AppServicesTab
              applicationId={id!}
              applicationName={application?.name ?? ""}
              serviceDeps={filteredServiceDeps}
              onUnlinkService={handleUnlinkService}
              unlinkingIds={isUnlinkingService}
            />
          </div>
        )}

        {activeTab === "cloud-mappings" && id && (
          <div className="space-y-4">
            {/* Sub-tab toggle */}
            <div className="flex items-center gap-1 p-1 bg-muted/40 rounded-lg w-fit border border-border">
              <button
                onClick={() => setCloudSubTab("accounts")}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  cloudSubTab === "accounts"
                    ? "bg-background text-foreground shadow-sm border border-border"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Cloud className="size-3.5" />
                Component Mapped Resources
                {mappedComponentResourcesCount > 0 && (
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                    cloudSubTab === "accounts" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400" : "bg-muted text-muted-foreground"
                  }`}>{mappedComponentResourcesCount}</span>
                )}
              </button>
            </div>

            {/* Sub-tab: Component Mapped Resources */}
            {cloudSubTab === "accounts" && (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Map component-level cloud resources to this application. Use <strong>+ Map</strong> inside an account to map service groups/resources.
                </p>
                <CMDBAwsMapping
                  applicationId={id}
                  onAccountUnlinked={() => { refetchComponents(); refetchAssets(); refetchLinkedAccounts(); }}
                />
              </div>
            )}

          </div>
        )}
      </div>
      )}

      <Modal
        open={showEditApp}
        onClose={() => setShowEditApp(false)}
        title="Edit Application"
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowEditApp(false)}
            onSubmit={handleSaveApp}
            submitLabel="Save"
          />
        }
      >
        <div className="space-y-4">
          <FormField label="Name" required>
            <FormInput
              value={editAppForm.name}
              onChange={(v) => setEditAppForm({ ...editAppForm, name: v })}
              placeholder="e.g. Internet Banking Portal"
            />
          </FormField>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Status" required>
              <FormSelect
                value={editAppForm.status}
                onChange={(v) => setEditAppForm({ ...editAppForm, status: v as ApplicationStatus })}
                options={[
                  { value: "OPERATIONAL", label: "Operational" },
                  { value: "UNDER_DEVELOPMENT", label: "Under Development" },
                  { value: "DISPOSITION", label: "Disposition" },
                  { value: "OTHER", label: "Other" },
                ]}
              />
            </FormField>
            <FormField label="Criticality" required>
              <FormSelect
                value={editAppForm.criticality}
                onChange={(v) => setEditAppForm({ ...editAppForm, criticality: v as ApplicationCriticality })}
                options={[
                  { value: "LOW", label: "Low" },
                  { value: "MEDIUM", label: "Medium" },
                  { value: "HIGH", label: "High" },
                  { value: "CRITICAL", label: "Critical" },
                ]}
              />
            </FormField>
          </div>
          <FormField label="App Code">
            <FormInput
              value={editAppForm.appCode}
              onChange={(v) => setEditAppForm({ ...editAppForm, appCode: v })}
              placeholder="e.g. IB, CBS, PAYROLL"
            />
          </FormField>
          <FormField label="Description">
            <FormTextarea
              value={editAppForm.description}
              onChange={(v) => setEditAppForm({ ...editAppForm, description: v })}
              placeholder="Optional description"
              rows={3}
            />
          </FormField>
        </div>
      </Modal>

      <Modal
        open={showWizard}
        onClose={resetWizard}
        title={
          editingMapping
            ? `Update Component${
                editingMapping.component?.title
                  ? ` — ${editingMapping.component.title}`
                  : ""
              }`
            : wizardPhase === "pick-component"
              ? "Add Components · Step 1 of 2: Pick Components"
              : "Add Components · Step 2 of 2: Attach Devices"
        }
        size="lg"
      >
        <div className="space-y-4">
          {wizardPhase === "pick-component" && (
            <>
              <div className="space-y-3">
                <FormField label="Components" required>
                  {/* Selected chips / summary */}
                  {selectedComponents.length > 0 && (
                    <div className="mb-2 flex flex-wrap items-center gap-1.5">
                      <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-400">
                        {selectedComponents.length} selected
                      </span>
                      {selectedComponents.map((c) => (
                        <span
                          key={c.id}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-500/15 text-indigo-700 dark:text-indigo-400 text-[11px] font-medium"
                        >
                          {c.title}
                          <Tip label="Remove" side="top">
                            <button
                              type="button"
                              onClick={() => removeSelectedComponent(c.id)}
                              className="hover:text-red-500 transition-colors"
                            >
                              <X className="size-3" />
                            </button>
                          </Tip>
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Search */}
                  <div className="relative mb-2">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search components by name…"
                      value={componentSearch}
                      onChange={(e) => setComponentSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
                    />
                  </div>

                  {/* Checkbox list */}
                  <div className="max-h-64 overflow-y-auto space-y-1 border border-border rounded-lg p-1">
                    {(() => {
                      const q = componentSearch.trim().toLowerCase();
                      // Exclude components already mapped to this application.
                      const mappedIds = new Set(
                        componentMappings.map((m) => m.componentId),
                      );
                      const list = wizardComponentList.filter(
                        (c) =>
                          !mappedIds.has(c.id) &&
                          (!q || c.title.toLowerCase().includes(q)),
                      );
                      if (list.length === 0) {
                        return (
                          <div className="text-center py-8 text-muted-foreground text-xs italic">
                            {componentSearch
                              ? `No components match “${componentSearch}”.`
                              : "All available components are already added."}
                          </div>
                        );
                      }
                      return list.map((c) => {
                        const checked = selectedComponents.some((x) => x.id === c.id);
                        return (
                          <button
                            key={c.id}
                            type="button"
                            onClick={() => toggleComponentSelection(c)}
                            className={`group/row w-full text-left px-2.5 py-2 rounded-lg text-sm transition-all flex items-center gap-3 border ${
                              checked
                                ? "bg-indigo-50 dark:bg-indigo-500/10 border-indigo-300/70 dark:border-indigo-500/40 shadow-sm"
                                : "border-transparent hover:bg-muted hover:border-border"
                            }`}
                          >
                            <span
                              className={`flex items-center justify-center size-[18px] rounded-md border-2 flex-shrink-0 transition-all duration-150 ${
                                checked
                                  ? "bg-indigo-600 border-indigo-600 text-white shadow-sm shadow-indigo-500/30 scale-105"
                                  : "border-slate-300 dark:border-slate-600 bg-background group-hover/row:border-indigo-400"
                              }`}
                            >
                              <Check
                                className={`size-3 transition-all duration-150 ${
                                  checked ? "scale-100 opacity-100" : "scale-0 opacity-0"
                                }`}
                                strokeWidth={3}
                              />
                            </span>
                            <span
                              className={`font-medium truncate flex-1 transition-colors ${
                                checked ? "text-indigo-700 dark:text-indigo-300" : ""
                              }`}
                            >
                              {c.title}
                            </span>
                            {c.componentType && (
                              <span
                                className={`text-[10px] font-bold tracking-wide flex-shrink-0 px-1.5 py-0.5 rounded ${
                                  checked
                                    ? "bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-400"
                                    : "bg-muted text-muted-foreground"
                                }`}
                              >
                                {c.componentType}
                              </span>
                            )}
                          </button>
                        );
                      });
                    })()}
                  </div>
                </FormField>

                <FormField label="Component Layer" required>
                  <FormSelect
                    value={selectedLayerId}
                    onChange={(v) => {
                      setSelectedLayerId(v);
                      setLayerManuallyChanged(true);
                    }}
                    placeholder={
                      sspLayers.length === 0
                        ? "No layers available"
                        : "Select a layer"
                    }
                    options={sspLayers.map((l) => ({
                      value: l.id,
                      label: l.displayName,
                    }))}
                  />
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    Auto-selected from the picked component — change it if needed.
                  </p>
                </FormField>

                {editingMapping && (() => {
                  const newSlug = (
                    sspLayers.find((l) => l.id === selectedLayerId)?.slug ?? ""
                  ).toLowerCase();
                  const originalSlug = (editingMapping.sspLayerSlug ?? "").toLowerCase();
                  const layerChanged = !!newSlug && newSlug !== originalSlug;
                  if (!layerChanged) return null;
                  return (
                    <FormField label="Reason for layer change" required>
                      <FormTextarea
                        value={layerOverrideReason}
                        onChange={setLayerOverrideReason}
                        placeholder="Explain why this component's layer is being reclassified"
                        rows={2}
                      />
                    </FormField>
                  );
                })()}

                {editingMapping && (
                  <FormField label="Notes">
                    <FormTextarea
                      value={componentAttachments[editingMapping.componentId]?.form.notes ?? ""}
                      onChange={(v) =>
                        setComponentAttachments((prev) => ({
                          ...prev,
                          [editingMapping.componentId]: {
                            ...(prev[editingMapping.componentId] ?? {
                              targetType: "DEVICE",
                              device: null,
                              resource: null,
                              cloudAccountFilter: "",
                              form: { ...DEFAULT_DEPLOYMENT_FORM },
                            }),
                            form: {
                              ...(prev[editingMapping.componentId]?.form ?? DEFAULT_DEPLOYMENT_FORM),
                              notes: v,
                            },
                          },
                        }))
                      }
                      placeholder="Optional notes for this mapping"
                      rows={2}
                    />
                  </FormField>
                )}

                {!editingMapping && (
                  <p className="text-xs text-muted-foreground">
                    Select one or more components, then click Continue to optionally attach a device or cloud resource to each.
                  </p>
                )}
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button onClick={resetWizard} className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm">Cancel</button>
                {editingMapping ? (
                  <button
                    onClick={handleUpdateMapping}
                    disabled={isSavingWizard || (sspLayers.length > 0 && !selectedLayerId)}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors text-sm"
                  >
                    {isSavingWizard && <Loader className="size-3.5 animate-spin" />}
                    Update Component
                  </button>
                ) : (
                  <button
                    onClick={() => setWizardPhase("add-device")}
                    disabled={
                      selectedComponents.length === 0 ||
                      (sspLayers.length > 0 && !selectedLayerId)
                    }
                    className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:hover:bg-indigo-600 transition-colors text-sm"
                  >
                    Continue
                  </button>
                )}
              </div>
            </>
          )}

          {wizardPhase === "add-device" && (
            <>
              <p className="text-xs text-muted-foreground">
                Attach a device or cloud resource to each component. All
                components must have one selected before you can add them.
              </p>

              <div className="space-y-2 max-h-[55vh] overflow-y-auto px-2 py-1.5">
                {selectedComponents.map((comp) => {
                  const isActive = selectedComponent?.id === comp.id;
                  const saved = componentAttachments[comp.id];
                  const liveDevice = isActive ? selectedDevice : saved?.device ?? null;
                  const liveResource = isActive
                    ? wizardSelectedResource
                    : saved?.resource ?? null;
                  const hasAttachment = !!(liveDevice || liveResource);
                  const summary = liveDevice
                    ? liveDevice.name
                    : liveResource
                      ? liveResource.resourceName || liveResource.resourceId
                      : "Select a device or cloud resource";
                  const tooltipText = isActive
                    ? `Click to collapse ${comp.title}`
                    : hasAttachment
                      ? `Click to change the device or cloud resource for ${comp.title}`
                      : `Click to attach a device or cloud resource to ${comp.title}`;
                  return (
                    <Tip key={comp.id} label={tooltipText} side="top" className="block w-full">
                    <div
                      className={`group/card rounded-lg border transition-all duration-200 hover:shadow-lg hover:shadow-indigo-500/20 hover:scale-[1.01] hover:ring-2 hover:ring-indigo-400/40 hover:border-indigo-400 dark:hover:border-indigo-400 ${
                        isActive
                          ? "border-indigo-500/50 bg-indigo-500/[0.03]"
                          : hasAttachment
                            ? "border-border"
                            : "border-amber-400/50 dark:border-amber-500/30"
                      }`}
                    >
                      {/* Card header */}
                      <button
                        type="button"
                        onClick={() => openComponentPicker(comp)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-left rounded-lg transition-colors group-hover/card:bg-indigo-500/[0.06]"
                      >
                        <div className="size-8 rounded-lg bg-indigo-500/10 flex items-center justify-center flex-shrink-0">
                          <Layers className="size-4 text-indigo-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate">{comp.title}</p>
                          <p
                            className={`text-[11px] truncate ${
                              hasAttachment
                                ? "text-indigo-600 dark:text-indigo-400 font-medium"
                                : "text-amber-600 dark:text-amber-400"
                            }`}
                          >
                            {summary}
                          </p>
                        </div>
                        {hasAttachment ? (
                          <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                            Attached
                          </span>
                        ) : (
                          <span className="flex-shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
                            Required
                          </span>
                        )}
                        {isActive ? (
                          <ChevronDown className="size-4 text-muted-foreground flex-shrink-0" />
                        ) : (
                          <ChevronRight className="size-4 text-muted-foreground flex-shrink-0" />
                        )}
                      </button>

                      {/* Active card body — device/cloud picker */}
                      {isActive && (
                        <div className="px-3 pb-3 pt-1 space-y-3 border-t border-border">
              {!wizardDeviceOnly && (
              <div className="flex gap-2">
                {(["DEVICE", "CLOUD"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      setWizardTargetType(t);
                      setSelectedDevice(null);
                      setWizardSelectedResource(null);
                      setWizardCloudAccountId("");
                      setWizardCloudResources([]);
                      setWizardResTypeFilter("");
                      setWizardResSearch("");
                      setWizardCloudAccountFilter("");
                    }}
                    className={`flex-1 px-3 py-2 rounded-lg border text-xs font-medium transition-colors flex items-center justify-center gap-1.5 ${
                      wizardTargetType === t
                        ? "border-indigo-50 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400"
                        : "border-border hover:bg-muted text-muted-foreground"
                    }`}
                  >
                    {t === "DEVICE" ? <><Server className="size-3.5" /> On-Prem</> : <><Cloud className="size-3.5" /> Cloud Account</>}
                  </button>
                ))}
              </div>
              )}

              {wizardTargetType === "DEVICE" && (
                <>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <input type="text" placeholder="Search devices…" value={serviceSearch}
                      onChange={(e) => setServiceSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm" />
                  </div>
                  <div className="max-h-40 overflow-y-auto space-y-1 border border-border rounded-lg p-1">
                    {ciOnpremList
                      .filter((d) => {
                        if (!serviceSearch) return true;
                        const q = serviceSearch.toLowerCase();
                        return d.name?.toLowerCase().includes(q) || d.ipAddress?.toLowerCase().includes(q);
                      })
                      .map((device) => (
                        <button key={device.id}
                          onClick={() => setSelectedDevice(selectedDevice?.id === device.id ? null : device)}
                          className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors flex items-center gap-2 ${
                            selectedDevice?.id === device.id
                              ? "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 border border-indigo-300"
                              : "hover:bg-muted"
                          }`}>
                          <Server className="size-3.5 flex-shrink-0 text-muted-foreground" />
                          <span className="font-medium truncate">{device.name}</span>
                          <span className="text-xs text-muted-foreground flex-shrink-0">{device.ipAddress ?? "—"}</span>
                        </button>
                      ))}
                  </div>
                </>
              )}

              {wizardTargetType === "CLOUD" && (
                <div className="space-y-3">
                  {linkedCloudAccounts.length > 1 && (
                    <FormField label="Cloud Account">
                      <FormSelect
                        value={wizardCloudAccountFilter}
                        onChange={(v) => {
                          setWizardCloudAccountFilter(v);
                          setWizardResTypeFilter("");
                          setWizardSelectedResource(null);
                        }}
                        options={[
                          { value: "", label: "All Linked Accounts" },
                          ...linkedCloudAccounts.map((a) => ({ value: a.id, label: `${a.accountName} (${a.provider})` })),
                        ]}
                      />
                    </FormField>
                  )}
                  <div className="flex gap-2">
                    <div className="w-40 flex-shrink-0">
                      <FormSelect
                        value={wizardResTypeFilter}
                        onChange={(v) => setWizardResTypeFilter(v)}
                        options={[
                          { value: "", label: "All Types" },
                          ...wizardResTypes.map((t) => ({ value: t.cloudIdentifier || t.awsIdentifier || "", label: t.serviceName })),
                        ]}
                      />
                    </div>
                    <div className="relative flex-1 min-w-0">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                      <input
                        type="text"
                        placeholder="Search resources…"
                        value={wizardResSearch}
                        onChange={(e) => setWizardResSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
                      />
                    </div>
                  </div>
                  <div className="max-h-44 overflow-y-auto space-y-1 border border-border rounded-lg p-1">
                    {wizardCloudResLoading ? (
                      <div className="flex items-center justify-center py-10 text-muted-foreground gap-2">
                        <Loader className="size-4 animate-spin text-indigo-500" />
                        <span className="text-xs font-medium">Searching cloud resources…</span>
                      </div>
                    ) : wizardCloudResources.length === 0 ? (
                      <div className="text-center py-10 text-muted-foreground text-xs italic">
                        No resources found. Try a different search or link a cloud account first.
                      </div>
                    ) : (
                      wizardCloudResources.map((r) => (
                        <button key={r.id} type="button"
                          onClick={() => {
                            setWizardSelectedResource(wizardSelectedResource?.id === r.id ? null : r);
                            if (wizardSelectedResource?.id !== r.id) {
                              const port = autoFillPort(r.cloudIdentifier || r.awsIdentifier || "", newDeploymentForm.port);
                              if (port) setNewDeploymentForm((f) => ({ ...f, port }));
                            }
                          }}
                          className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                            wizardSelectedResource?.id === r.id
                              ? "bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-300 dark:border-indigo-500/40"
                              : "hover:bg-muted"
                          }`}>
                          <Database className="size-3.5 flex-shrink-0 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{r.resourceName || r.resourceId}</p>
                            <p className="text-[10px] text-muted-foreground">{r.serviceName} · {r.region}</p>
                          </div>
                          {r.status && <StatusBadge status={r.status} />}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {(selectedDevice || wizardSelectedResource) && (
                <div className="grid grid-cols-2 gap-3">
                  <FormField label="Port">
                    <FormInput
                      type="number"
                      value={newDeploymentForm.port}
                      onChange={(v) => setNewDeploymentForm({ ...newDeploymentForm, port: v })}
                      placeholder="e.g. 3306"
                    />
                  </FormField>
                  <FormField label="Role" required>
                    <FormSelect value={newDeploymentForm.role}
                      onChange={(v) => setNewDeploymentForm({ ...newDeploymentForm, role: v as any })}
                      options={[
                        { value: "PRIMARY", label: "Primary" }, { value: "BACKUP", label: "Backup" },
                        { value: "STANDBY", label: "Standby" }, { value: "READ_REPLICA", label: "Read Replica" },
                        { value: "DR", label: "DR" }, { value: "ACTIVE", label: "Active" },
                        { value: "PASSIVE", label: "Passive" },
                      ]} />
                  </FormField>
                  <FormField label="Protocol">
                    <FormSelect value={newDeploymentForm.protocol}
                      onChange={(v) => setNewDeploymentForm({ ...newDeploymentForm, protocol: v as any })}
                      options={[
                        { value: "TCP", label: "TCP" }, { value: "UDP", label: "UDP" },
                        { value: "HTTP", label: "HTTP" }, { value: "HTTPS", label: "HTTPS" },
                        { value: "GRPC", label: "gRPC" },
                      ]} />
                  </FormField>
                  <FormField label="Status" required>
                    <FormSelect value={newDeploymentForm.status}
                      onChange={(v) => setNewDeploymentForm({ ...newDeploymentForm, status: v as AssetStatus })}
                      options={[
                        { value: "RUNNING", label: "Running" }, { value: "STOPPED", label: "Stopped" },
                        { value: "STARTING", label: "Starting" }, { value: "DEGRADED", label: "Degraded" },
                        { value: "MAINTENANCE", label: "Maintenance" }, { value: "UNKNOWN", label: "Unknown" },
                      ]} />
                  </FormField>
                </div>
              )}
                        </div>
                      )}
                    </div>
                    </Tip>
                  );
                })}
              </div>
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-border">
                {wizardSingleComponent ? (
                  <span />
                ) : (
                  <button onClick={() => setWizardPhase("pick-component")} disabled={isSavingWizard} className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm disabled:opacity-50">Back</button>
                )}
                <div className="flex gap-3">
                  <button onClick={resetWizard} disabled={isSavingWizard} className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm disabled:opacity-50">Cancel</button>
                  <Tip
                    label={
                      componentsMissingAttachment.length > 0
                        ? `Select a device or cloud resource for: ${componentsMissingAttachment
                            .map((c) => c.title)
                            .join(", ")}`
                        : undefined
                    }
                    side="top"
                  >
                    <button
                      onClick={handleSaveOnPremMapping}
                      disabled={
                        isSavingWizard ||
                        selectedComponents.length === 0 ||
                        componentsMissingAttachment.length > 0
                      }
                      className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSavingWizard && <Loader className="size-3.5 animate-spin" />}
                      Add {selectedComponents.length} Component{selectedComponents.length !== 1 ? "s" : ""}
                    </button>
                  </Tip>
                </div>
              </div>
            </>
          )}
        </div>
      </Modal>

      <Modal
        open={!!editTarget}
        onClose={() => { if (!isSavingEdit) setEditTarget(null); }}
        title={`Edit — ${editTarget?.ciName ?? ""}`}
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setEditTarget(null)}
            onSubmit={handleSaveEdit}
            submitLabel={isSavingEdit ? "Saving…" : "Save"}
            submitDisabled={isSavingEdit}
          />
        }
      >
        {editTarget && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Port">
                <FormInput
                  type="number"
                  value={editForm.port}
                  onChange={(v) => setEditForm({ ...editForm, port: v })}
                  placeholder="e.g. 8080"
                />
              </FormField>
              <FormField label="Role" required>
                <FormSelect
                  value={editForm.role}
                  onChange={(v) => setEditForm({ ...editForm, role: v as AssetRole })}
                  options={[
                    { value: "PRIMARY", label: "Primary" },
                    { value: "BACKUP", label: "Backup" },
                    { value: "STANDBY", label: "Standby" },
                    { value: "READ_REPLICA", label: "Read Replica" },
                    { value: "DR", label: "DR" },
                    { value: "ACTIVE", label: "Active" },
                    { value: "PASSIVE", label: "Passive" },
                  ]}
                />
              </FormField>
              <FormField label="Protocol">
                <FormSelect
                  value={editForm.protocol}
                  onChange={(v) => setEditForm({ ...editForm, protocol: v as AssetProtocol })}
                  options={[
                    { value: "TCP", label: "TCP" },
                    { value: "UDP", label: "UDP" },
                    { value: "HTTP", label: "HTTP" },
                    { value: "HTTPS", label: "HTTPS" },
                    { value: "GRPC", label: "gRPC" },
                  ]}
                />
              </FormField>
              <FormField label="Status">
                <FormSelect
                  value={editForm.status}
                  onChange={(v) => setEditForm({ ...editForm, status: v as AssetStatus })}
                  options={[
                    { value: "RUNNING", label: "Running" },
                    { value: "STOPPED", label: "Stopped" },
                    { value: "STARTING", label: "Starting" },
                    { value: "DEGRADED", label: "Degraded" },
                    { value: "MAINTENANCE", label: "Maintenance" },
                    { value: "UNKNOWN", label: "Unknown" },
                  ]}
                />
              </FormField>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={showLinkService}
        onClose={() => { if (!isLinkingService) setShowLinkService(false); }}
        title="Link Service"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowLinkService(false)}
            onSubmit={handleLinkService}
            submitLabel={isLinkingService ? "Linking…" : "Link Service"}
            submitDisabled={!selectedService || isLinkingService}
          />
        }
      >
        <div className="space-y-4">
          <FormField label="Dependency Type" required>
            <FormSelect
              value={serviceDependencyType}
              onChange={(v) => setServiceDependencyType(v as AppServiceDependencyType)}
              options={[
                { value: "REQUIRED", label: "Required" },
                { value: "OPTIONAL", label: "Optional" },
              ]}
            />
          </FormField>
          <FormField label="Service" required>
            <div className="relative mb-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search services…"
                value={serviceSearch}
                onChange={(e) => setServiceSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
              />
            </div>
            <div className="max-h-56 overflow-y-auto space-y-1.5 border border-border rounded-lg p-1">
              {allServices
                .filter((s) => !serviceDeps.some((d) => d.serviceId === s.id))
                .filter((s) => !serviceSearch || s.name.toLowerCase().includes(serviceSearch.toLowerCase()))
                .map((svc) => (
                  <button
                    key={svc.id}
                    onClick={() => { setSelectedService(svc); initLinkServiceAssets(svc); }}
                    className={`w-full text-left p-3 rounded-md transition-colors ${
                      selectedService?.id === svc.id
                        ? "bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-300 dark:border-indigo-500/30"
                        : "hover:bg-muted"
                    }`}
                  >
                    <p className="font-medium text-sm">{svc.name}</p>
                  </button>
                ))}
            </div>
          </FormField>

          {/* Assets preview for the selected service */}
          {selectedService && (() => {
            const allAssets = Array.from(
              new Map(
                (selectedService.components ?? [])
                  .flatMap((c) =>
                    (c.deployments ?? []).map((d) => ({
                      id: d.assetId,
                      name: d.ciName || d.awsResourceName || d.cloudResourceName || d.ciIdentifier || d.awsResourceIdentifier || "—",
                      identifier: d.ciIdentifier || d.awsResourceIdentifier || d.ciIpAddress || null,
                      role: d.role,
                      status: d.status,
                    })),
                  )
                  .map((a) => [a.id, a]),
              ).values(),
            );
            if (allAssets.length === 0) return null;
            const filtered = linkServiceAssetSearch
              ? allAssets.filter((a) =>
                  a.name.toLowerCase().includes(linkServiceAssetSearch.toLowerCase()) ||
                  (a.identifier ?? "").toLowerCase().includes(linkServiceAssetSearch.toLowerCase()),
                )
              : allAssets;
            const checkedCount = allAssets.filter((a) => linkServiceAssetChecks[a.id]).length;
            const allChecked = checkedCount === allAssets.length;
            const someChecked = checkedCount > 0 && checkedCount < allAssets.length;
            return (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-muted-foreground tracking-wider">
                    Assets in this service ({allAssets.length})
                  </p>
                  <span className="text-[11px] text-muted-foreground">
                    {checkedCount}/{allAssets.length} selected
                  </span>
                </div>
                {/* Search for 100+ assets */}
                {allAssets.length > 8 && (
                  <div className="relative mb-2">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Filter assets…"
                      value={linkServiceAssetSearch}
                      onChange={(e) => setLinkServiceAssetSearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-1.5 text-xs border border-border rounded-md bg-background focus:border-indigo-500 outline-none"
                    />
                  </div>
                )}
                <div className="border border-border rounded-lg overflow-hidden">
                  {/* Select-all header row */}
                  <label className="flex items-center gap-3 px-3 py-2 bg-muted/50 border-b border-border cursor-pointer hover:bg-muted transition-colors">
                    <input
                      type="checkbox"
                      checked={allChecked}
                      ref={(el) => { if (el) el.indeterminate = someChecked; }}
                      onChange={() => {
                        const next = !allChecked;
                        const updated: Record<string, boolean> = {};
                        allAssets.forEach((a) => { updated[a.id] = next; });
                        setLinkServiceAssetChecks(updated);
                      }}
                      className="size-3.5 accent-indigo-600 cursor-pointer"
                    />
                    <span className="text-[11px] font-semibold text-muted-foreground">Select all</span>
                  </label>
                  {/* Asset rows — max-h-48 handles 100+ with scroll */}
                  <div className="max-h-48 overflow-y-auto divide-y divide-border">
                    {filtered.length === 0 ? (
                      <p className="px-3 py-3 text-xs text-muted-foreground text-center">No assets match filter.</p>
                    ) : (
                      filtered.map((asset) => (
                        <label
                          key={asset.id}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={!!linkServiceAssetChecks[asset.id]}
                            onChange={(e) =>
                              setLinkServiceAssetChecks((prev) => ({ ...prev, [asset.id]: e.target.checked }))
                            }
                            className="size-3.5 accent-indigo-600 cursor-pointer shrink-0"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground truncate">{asset.name}</p>
                            {asset.identifier && (
                              <p className="text-[11px] text-muted-foreground">{asset.identifier}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                              {asset.role}
                            </span>
                            <span className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                              {asset.status}
                            </span>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                </div>
                {checkedCount < allAssets.length && (
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1.5">
                    {allAssets.length - checkedCount} asset(s) will be excluded after linking.
                  </p>
                )}
              </div>
            );
          })()}
        </div>
      </Modal>

      <Modal
        open={!!cloudAddTarget}
        onClose={() => { if (!cloudAddSaving) setCloudAddTarget(null); }}
        title={`Add Cloud Resource — ${cloudAddTarget?.component.title ?? ""}`}
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setCloudAddTarget(null)}
            onSubmit={handleConfirmAddCloudResource}
            submitLabel={cloudAddSaving ? "Saving…" : "Save"}
            submitDisabled={!selectedCloudAddResourceId || cloudAddSaving}
          />
        }
      >
        <div className="space-y-3">
          {linkedCloudAccounts.length > 1 && (
            <FormField label="Cloud Account">
              <FormSelect
                value={cloudAddAccountFilter}
                onChange={(v) => {
                  setCloudAddAccountFilter(v);
                  setCloudAddTypeFilter("");
                  setSelectedCloudAddResourceId("");
                }}
                options={[
                  { value: "", label: "All Linked Accounts" },
                  ...linkedCloudAccounts.map((a) => ({ value: a.id, label: `${a.accountName} (${a.provider})` })),
                ]}
              />
            </FormField>
          )}
          <div className="flex gap-2">
            <div className="w-40 flex-shrink-0">
              <FormSelect
                value={cloudAddTypeFilter}
                onChange={setCloudAddTypeFilter}
                options={[
                  { value: "", label: "All Types" },
                  ...cloudAddTypes.map((t) => ({ value: t.cloudIdentifier || t.awsIdentifier || "", label: t.serviceName })),
                ]}
              />
            </div>
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search resources…"
                value={cloudAddSearch}
                onChange={(e) => setCloudAddSearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-border rounded-lg bg-background focus:border-indigo-500 outline-none text-sm"
              />
            </div>
          </div>

          {cloudAddLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground gap-2 border border-border rounded-lg">
              <Loader className="size-4 animate-spin text-indigo-500" />
              <span className="text-xs font-medium">Searching cloud resources…</span>
            </div>
          ) : cloudAddResources.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground text-xs italic border border-border rounded-lg">
              No resources found.
            </div>
          ) : (
            <div className="max-h-56 overflow-y-auto space-y-1 border border-border rounded-lg p-1">
              {cloudAddResources.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  onClick={() => setSelectedCloudAddResourceId(r.id)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2.5 ${
                    selectedCloudAddResourceId === r.id
                      ? "bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-300 dark:border-indigo-500/40"
                      : "hover:bg-muted"
                  }`}
                >
                  <Cloud className="size-3.5 flex-shrink-0 text-indigo-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{r.resourceName || r.resourceId}</p>
                    <p className="text-[10px] font-mono text-muted-foreground truncate">{r.resourceId}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {r.region}{r.status ? ` · ${r.status}` : ""}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>

      {/* ── Edit Component Layer Modal ─────────────────────────────────────── */}
      <Modal
        open={!!layerEditMapping}
        onClose={closeEditLayer}
        title="Update Component"
        size="md"
      >
        {layerEditMapping && (
          <div className="space-y-4">
            <div className="px-3 py-2 bg-muted/50 rounded-lg text-sm">
              Component:{" "}
              <span className="font-semibold">
                {layerEditMapping.component.title}
              </span>
              {layerEditMapping.component.componentType && (
                <span className="ml-2 text-[10px] font-semibold text-slate-500 dark:text-slate-400 tracking-wide">
                  {layerEditMapping.component.componentType}
                </span>
              )}
            </div>
            <FormField label="Component Layer" required>
              <FormSelect
                value={layerEditId}
                onChange={setLayerEditId}
                placeholder={
                  sspLayers.length === 0
                    ? "No layers available"
                    : "Select a layer"
                }
                options={sspLayers.map((l) => ({
                  value: l.id,
                  label: l.displayName,
                }))}
              />
            </FormField>
            {(() => {
              const newSlug = (
                sspLayers.find((l) => l.id === layerEditId)?.slug ?? ""
              ).toLowerCase();
              const originalSlug = (
                layerEditMapping.sspLayerSlug ?? ""
              ).toLowerCase();
              const layerChanged = !!newSlug && newSlug !== originalSlug;
              if (!layerChanged) return null;
              return (
                <FormField label="Reason" required>
                  <FormTextarea
                    value={layerEditReason}
                    onChange={setLayerEditReason}
                    placeholder="Explain why this component's layer is being reclassified"
                    rows={3}
                  />
                </FormField>
              );
            })()}
            <div className="flex justify-end gap-3 pt-2 border-t border-border">
              <button
                onClick={closeEditLayer}
                disabled={isSavingLayerEdit}
                className="px-4 py-2 border border-border rounded-lg hover:bg-muted transition-colors text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditLayer}
                disabled={isSavingLayerEdit || !layerEditId}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm disabled:opacity-50"
              >
                {isSavingLayerEdit && (
                  <Loader className="size-3.5 animate-spin" />
                )}
                Save
              </button>
            </div>
          </div>
        )}
      </Modal>

      <DeleteConfirmDialog
        open={!!deletingDeployment}
        type="Deployment Record"
        name={deletingDeployment?.name || ""}
        deleting={Object.values(isRemovingDeployment).some(Boolean)}
        onCancel={() => setDeletingDeployment(null)}
        onConfirm={confirmRemoveDeployment}
      />
      <DeleteConfirmDialog
        open={!!deletingComponent}
        type="Component"
        name={deletingComponent?.component.title || ""}
        deleting={Object.values(isRemovingComp).some(Boolean)}
        onCancel={() => setDeletingComponent(null)}
        onConfirm={confirmRemoveComponent}
      />
      <DeleteConfirmDialog
        open={!!deletingService}
        type="Service Dependency"
        name={deletingService?.service.name || ""}
        deleting={Object.values(isUnlinkingService).some(Boolean)}
        onCancel={() => setDeletingService(null)}
        onConfirm={confirmUnlinkService}
      />

      <Modal
        open={showDeleteApp}
        onClose={() => { if (!deletingApp) setShowDeleteApp(false); }}
        title="Delete Application?"
        size="md"
        footer={
          <div className="flex w-full items-center justify-end gap-3">
            <button
              onClick={() => setShowDeleteApp(false)}
              disabled={deletingApp}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDeleteApplication}
              disabled={deletingApp}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-40"
            >
              {deletingApp ? <span className="inline-flex items-center gap-2"><Loader className="size-3.5 animate-spin" /> Deleting</span> : "Delete"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-muted-foreground">
          This will permanently remove all CMDB data linked to this application - components, assets, cloud accounts, and service mappings. This cannot be undone.
        </p>
      </Modal>
    </div>
  );
}
